import { essayMinutesFor, MIN_ESSAY_MINUTES, MAX_ESSAY_MINUTES } from './practiceHelpers.js';

// These snapshots belong only to the mounted reader's memory. In particular,
// schoolSubmission carries an actual Promise and must never be serialised to
// local/sessionStorage, a URL, Firestore, or another user's page session.
export function essayPageMinutes(item, override) {
    return Number.isInteger(override) && override >= MIN_ESSAY_MINUTES && override <= MAX_ESSAY_MINUTES
        ? override : essayMinutesFor(item);
}

export function createEssayPageSession({ ownerUid = null, packId = null, promptId = null, minutes }) {
    return {
        ownerUid, packId, promptId, minutes,
        phase: 'setup', answer: '', remaining: minutes * 60, deadlineAt: null,
        schoolAttemptId: null, schoolSubmission: null,
    };
}

// Re-entering a running attempt uses its original deadline. No helper starts an
// attempt, generates an ID, saves an answer, or reveals a model automatically.
export function advanceEssayPageSession(session, now) {
    if (session.phase !== 'running') return session;
    const remaining = Math.max(0, Math.min(session.minutes * 60, Math.ceil((session.deadlineAt - now) / 1000)));
    return { ...session, remaining, phase: remaining > 0 ? 'running' : 'done' };
}

export function restoreEssayPageSession(candidate, { ownerUid = null, packId = null, promptId = null, minutes }, now = Date.now()) {
    if (!candidate || candidate.ownerUid !== ownerUid || candidate.packId !== packId || candidate.promptId !== promptId || candidate.minutes !== minutes) return null;
    if (ownerUid !== null && (typeof ownerUid !== 'string' || !ownerUid.length)) return null;
    if (!Number.isInteger(minutes) || minutes < MIN_ESSAY_MINUTES || minutes > MAX_ESSAY_MINUTES) return null;
    if (!['setup', 'running', 'done'].includes(candidate.phase) || typeof candidate.answer !== 'string') return null;
    if (!Number.isInteger(candidate.remaining) || candidate.remaining < 0 || candidate.remaining > minutes * 60) return null;
    const attemptId = candidate.schoolAttemptId;
    if (attemptId !== null && (typeof attemptId !== 'string' || !attemptId.length)) return null;
    if (candidate.phase !== 'setup' && !attemptId) return null;
    if (candidate.deadlineAt !== null && (!Number.isFinite(candidate.deadlineAt) || candidate.deadlineAt <= 0)) return null;
    if (candidate.phase === 'running' && !Number.isFinite(candidate.deadlineAt)) return null;
    const submission = candidate.schoolSubmission;
    if (submission !== null && (!submission || typeof submission.promise?.then !== 'function'
        || !['pending', 'saved', 'skipped', 'error'].includes(submission.status) || typeof submission.message !== 'string')) return null;
    if (!Number.isFinite(now)) return null;
    return advanceEssayPageSession({
        ownerUid, packId, promptId, minutes,
        phase: candidate.phase, answer: candidate.answer, remaining: candidate.remaining,
        deadlineAt: candidate.deadlineAt, schoolAttemptId: attemptId,
        // Preserve this shared handle by identity: a pending save can settle while
        // its editor is temporarily unmounted without causing a second write.
        schoolSubmission: submission,
    }, now);
}

// Check at callable dispatch time, including inside deferred work. Anonymous or
// changed sessions must never send an earlier owner's private essay as the new user.
export function essaySchoolOwnerMatches(ownerUid, currentUid) {
    return typeof ownerUid === 'string' && ownerUid.length > 0 && ownerUid === currentUid;
}
